/**
 * Helpers for working with the WebAuthn / Passkey browser API.
 * The server returns base64url-encoded fields in JSON; the browser API needs
 * ArrayBuffers. These functions translate between the two.
 */

function b64uToBuffer(b64u) {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufferToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Decode server-issued PublicKeyCredentialCreationOptions for navigator.credentials.create(). */
export function decodeCreationOptions(opts) {
  const pk = opts.publicKey;
  return {
    publicKey: {
      ...pk,
      challenge: b64uToBuffer(pk.challenge),
      user: { ...pk.user, id: b64uToBuffer(pk.user.id) },
      excludeCredentials: (pk.excludeCredentials || []).map((c) => ({
        ...c,
        id: b64uToBuffer(c.id),
      })),
    },
  };
}

/** Decode server-issued PublicKeyCredentialRequestOptions for navigator.credentials.get(). */
export function decodeRequestOptions(opts) {
  const pk = opts.publicKey;
  return {
    publicKey: {
      ...pk,
      challenge: b64uToBuffer(pk.challenge),
      allowCredentials: (pk.allowCredentials || []).map((c) => ({
        ...c,
        id: b64uToBuffer(c.id),
      })),
    },
  };
}

/** Encode a created/asserted credential back into JSON the server can verify. */
export function encodeCredential(cred) {
  const out = {
    id: cred.id,
    rawId: bufferToB64u(cred.rawId),
    type: cred.type,
    response: {},
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  };
  const r = cred.response;
  if (r.attestationObject) out.response.attestationObject = bufferToB64u(r.attestationObject);
  if (r.clientDataJSON) out.response.clientDataJSON = bufferToB64u(r.clientDataJSON);
  if (r.authenticatorData) out.response.authenticatorData = bufferToB64u(r.authenticatorData);
  if (r.signature) out.response.signature = bufferToB64u(r.signature);
  if (r.userHandle) out.response.userHandle = bufferToB64u(r.userHandle);
  return out;
}

export const passkeysSupported = () =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials;
